import React, { useState, useEffect } from 'react';
import { Form, Col, Row } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { TextInput, RadioInput, SelectInput } from '../../../Helpers/Inputs';
import States from '../../../Helpers/Lists/States';
import { useForm } from 'react-hook-form/dist/react-hook-form.ie11';
import { object, mixed, string } from 'yup';
import Summary from '../../Common/Appointment/Summary';
import HelpSection from '../../Common/Appointment/HelpSection';
import CovrButton from '../../../Helpers/Buttons/CovrButton';
import { InputMaskPhone } from "../../../Helpers/Formatters";
import Tooltip from '@material-ui/core/Tooltip';
import InfoIcon from '@material-ui/icons/Info';
import { getSessionStorageItem, SESSION_STORAGE_ACCOUNT_KEY } from '../../../../sessionStorage';

import '../Haven.scss';

export default function LGAApplyDialog(props) {

  const { quoteFields, basicInfo, quoteResult } = props;

  const { register, errors, handleSubmit, setValue } = useForm({
    validationSchema: LGAApplyDialog.ValidationSchema,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  // inputs 
  const [loading, setLoading] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [lastFourSSN, setlastFourSSN] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState(props.basicInfo.state || '');
  const [zip, setZip] = useState('');
  const [primaryPhone, setPrimaryPhone] = useState('');
  const [streetAddress, setStreetAddress] = useState('');
  const [email, setEmail] = useState(props.basicInfo.email || '');
  const [phoneNumberType, setPhoneNumberType] = useState('');

  useEffect(() => {
    register({ name: 'state' });
    setValue('state', state);
    register({ name: 'phoneNumberType' });
    setValue('phoneNumberType', phoneNumberType);
  }, [register]);

  const handleValueChange = (field, value, useStateSetValue) => {
    setValue(field, value);
    useStateSetValue(value);
  };

  const onSubmit = () => {
    setLoading(true);
    props.logQuote(email, null, props.quoteResult);

    const covrServicesUrl = process.env.REACT_APP_COVR_SERVICES_URL;

    const submissionBody = {
      "firstName": firstName,
      "lastName": lastName,
      "dob": basicInfo.birthdate,
      "gender": basicInfo.gender,
      "ssn": lastFourSSN,
      "addressLine1": streetAddress,
      "city": city,
      "state": state,
      "zipCode": zip,
      "insuranceAmount": quoteFields.coverageAmount,
      "coveragePeriod": quoteFields.termLength,
      "insurancePremium": quoteResult.monthlyRate,
      "waiveOffPremiumRider": false,
      "primaryPhoneNumber": primaryPhone,
      "primaryPhoneLocation": phoneNumberType,
      "email": email,
      "product": `OPTerm ${quoteFields.termLength}`,
      "uwClass": quoteFields.healthClass,
      "tobacco": basicInfo.tobacco,
    };

    // make lga submit call
    fetch(`${covrServicesUrl}/api/LGA/LGAHandoffSubmit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'SessionId': getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY).sessionId
      },
      body: JSON.stringify(submissionBody),
    })
      .then(res => res.json())
      .then(json => {
        console.log(json);

        if (json.success == true) {
          var redirect = json.data.loginUrl;

          window.location = redirect;
        }
        else if (json.success == false) {
          setLoading(false);
        }
      })
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Row className="lgaModalInputs">
        <Col xs={12} md={6}>
          <h4 className="mb-4">Your Information</h4>
          <Form.Group>
            <TextInput
              name="firstName"
              id="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              label="First Name"
              inputRef={register}
              errors={errors.firstName}
            />
          </Form.Group>

          <Form.Group>
            <TextInput
              name="lastName"
              id="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              label="Last Name"
              inputRef={register}
              errors={errors.lastName}
            />
          </Form.Group>

          <Form.Group>
            <TextInput
              name="lastFourSSN"
              id="lastFourSSN"
              value={lastFourSSN}
              onChange={(e) => setlastFourSSN(e.target.value)}
              label="Last Four Digits of Your Social"
              inputRef={register}
              errors={errors.lastFourSSN}
              InputProps={{
                endAdornment: <Tooltip title={'The last four digits of your SS# is used to verify identity and helps expedite your life insurance application'} placement="top"><InfoIcon /></Tooltip>
              }}
            />
          </Form.Group>

          <div className="mb-3">
            <b>Address</b>
          </div>

          <Form.Group>
            <TextInput
              name="streetAddress"
              id="streetAddress"
              value={streetAddress}
              onChange={(e) => setStreetAddress(e.target.value)}
              label="Street Address"
              inputRef={register}
              errors={errors.streetAddress}
            />
          </Form.Group>

          <Form.Group>
            <TextInput
              name="city"
              id="city"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              label="City"
              inputRef={register}
              errors={errors.city}
            />
          </Form.Group>

          <Row>
            <Col>
              <Form.Group>
                <SelectInput
                  name="state"
                  id="state"
                  value={state}
                  onChange={(e) => handleValueChange('state', e.target.value, setState)}
                  label="State"
                  selectOptions={States()}
                  errors={errors.state}
                />
              </Form.Group>
            </Col>

            <Col>
              <Form.Group>
                <TextInput
                  name="zip"
                  id="zip"
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                  label="Zip"
                  inputRef={register}
                  errors={errors.zip}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group>
            <TextInput
              name="primaryPhone"
              id="primaryPhone"
              value={primaryPhone}
              onChange={(e) => setPrimaryPhone(e.target.value)}
              label="Primary Phone"
              inputRef={register}
              errors={errors.primaryPhone}
              InputProps={{
                inputComponent: InputMaskPhone,
              }}
            />
          </Form.Group>

          <Form.Group>
            <RadioInput
              name="phoneNumberType"
              id="phoneNumberType"
              label="Phone Type"
              value={phoneNumberType}
              radios={[{ value: "Cell", label: "Cell Phone" }, { value: "Work", label: "Work Phone" }]}
              onChange={e => handleValueChange('phoneNumberType', e.target.value, setPhoneNumberType)}
              errors={errors.phoneNumberType}
              inputRef={props.register}
            />
          </Form.Group>


          <Form.Group>
            <TextInput
              name="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              label="Email"
              inputRef={register}
              errors={errors.email}
            />
          </Form.Group>

          <Form.Group>
            <CovrButton
              name="START APPLICATION"
              loading={loading}
            />
          </Form.Group>
        </Col>
        <Col xs={12} md={6}>
          <Summary
            title={'Quote Summary'}
            coverageAmount={quoteFields.coverageAmount}
            carrier={'LGA'}
            termLength={`${quoteFields.termLength} Year Term`}
            premium={quoteResult.monthlyRate}
            basicInfo={basicInfo}
            carrierLogoUrl='/logos/LGA-logo.png'
          />
          <div className="large-screen">
            <HelpSection />
          </div>
        </Col>
      </Row>
    </Form>
  );
}

LGAApplyDialog.propTypes = {
  logQuote: PropTypes.func.isRequired,
  quoteResult: PropTypes.object.isRequired,
};

LGAApplyDialog.ValidationSchema = object().shape({
  email: string()
    .email('Email address must be valid.')
    .required('Email is required'),
  firstName: string()
    .matches(/^\s*$|^[ a-zA-ZÀ-ÖØ-öø-ÿ'.-]+$/, 'Numbers not allowed.')
    .required('First Name is required.'),
  lastName: string()
    .matches(/^\s*$|^[ a-zA-ZÀ-ÖØ-öø-ÿ'.-]+$/, 'Numbers not allowed.')
    .required('Last Name is required.'),
  email: string().required('Email address is required.').email('Email address must be valid.'),
  city: string()
    .matches(/^\s*$|^[ a-zA-ZÀ-ÖØ-öø-ÿ'.-]+$/, 'Numbers not allowed.')
    .required('City is required.'),
  state: string().required('State is required.'),
  streetAddress: string().required('Street Address is required.'),
  primaryPhone: string()
    .required('Phone number is required.')
    .matches(/^\(\d{3}\)\s\d{3}-\d{4}$/, 'Phone must be formatted as (123) 456-7890'),
  zip: string()
    .required('Zip code is required.')
    .matches(/^[0-9]{5}$/, 'Zip code must be 5 number characters.'),
  phoneNumberType: string().required('Phone number type is required.'),
  lastFourSSN: string()
    .required('Last four of SSN are required.')
    .matches(/^[0-9]{4}$/, 'Last four of SSN must be 4 number characters.')
});



// WEBPACK FOOTER //
// ./src/components/Quoters/Digital/LGA/LGAApplyDialog.js